import { html, type TemplateResult } from 'lit-html'
import { RELIC_GLYPH, type api, type ShareCard } from '@guild/core'

import type { App } from '../app.ts'
import { chartCard } from '../chart-card.ts'
import { dayStore } from '../day.ts'
import { inboxStore, loadInbox, updateSummary } from '../inbox.ts'
import { sliceGrid } from '../map.ts'
import { noteAtlas, noticeStore, openNotice, type Notice } from '../notices.ts'
import { defineScreen } from '../screen.ts'
import { focusSlip, practiceSlip, slip } from '../slip.ts'
import type { Store } from '../store.ts'
import {
  codeNameLine,
  errorText,
  fragmentText,
  islandName,
  letterProse,
  relicName,
} from '../text.ts'
import { reveal, stopReveal } from '../typewriter.ts'

/**
 * The post (#56).
 *
 * A letter arrives sealed: a code name and a day, and nothing else, because
 * that is all the contract carries. Opening it shows the body rendered in the
 * *reader's* locale from the ids it was stored as, and its attachments as torn
 * corners.
 *
 * What the reader may do to it is the server's answer, not this screen's
 * guess: `canChart` and `canMark` are both false once the island the letter
 * describes has finalised, which is the same rule for a letter delayed in the
 * post and for one read too late (Q16.2, Q11.3).
 *
 * Above the letters, the notices from the Keeper of the Rolls (Q32.2): one
 * for each island the player walked that went under in the last seven days,
 * sealed like a letter and signed by the persona the Guild already speaks
 * through. Opened, it says the island has gone under and shows the finished
 * chart card, the same one the day's end draws, with the verb that copies it.
 */

type State = {
  /** Day to day number, so an envelope can say "Day 4" and not a date. */
  dayNumbers: ReadonlyMap<string, number>
  open: api.ApiInboxLetter | null
  prose: string
  shown: number
  problem: string | null
  busy: boolean
  loaded: boolean
  /** The practice shore's word on the letter just opened, as a `ui` key (#134). */
  note: string | null
  /** The notice open, by the island's day, and its card once it has arrived. */
  openNotice: string | null
  noticeCard: ShareCard | null
  noticeText: string | null
  noticeCopied: boolean
}

const initial: State = {
  dayNumbers: new Map(),
  open: null,
  prose: '',
  shown: 0,
  problem: null,
  busy: false,
  loaded: false,
  note: null,
  openNotice: null,
  noticeCard: null,
  noticeText: null,
  noticeCopied: false,
}

export const postScreen = defineScreen<State>({
  id: 'post',
  initial,

  async enter(app, route, store, current) {
    stopReveal()
    store.set({
      problem: null,
      open: null,
      prose: '',
      shown: 0,
      note: null,
      openNotice: null,
      noticeCard: null,
      noticeText: null,
      noticeCopied: false,
    })
    // The summaries are the shell's, shared with the mark beside
    // `[ The Post ]` (#103); the Atlas is here for day numbers, and for the
    // notices from the Rolls (Q32.2), which are read off it with today's day.
    //
    // Asked again on every arrival, not only when nothing has been asked yet:
    // the boot fetch is for the mark, and a tab left open across the 00:00 UTC
    // job would otherwise show yesterday's list for as long as it stayed open
    // (#116). It is a short list of sealed envelopes.
    const [, atlas] = await Promise.all([loadInbox(app), app.api.atlas()])
    if (!current()) return
    const dayNumbers = new Map<string, number>()
    if (atlas.ok) {
      for (const island of atlas.value.islands) {
        if (island.dayNumber !== null) dayNumbers.set(island.day, island.dayNumber)
      }
      // Today is the server's, never the clock's (Q17.1): until the day store
      // has it, the boot's own fill reads the notices off the same Atlas.
      const today = dayStore.get().today?.day
      if (today !== undefined) noteAtlas(app, atlas.value, today)
    }
    store.set({ loaded: true, dayNumbers })
    if (route.screen === 'post' && route.publicId !== null) {
      await openLetter(app, store, route.publicId)
    }
  },

  keydown(event, state, _app, store) {
    if (state.note !== null && event.key === 'Escape') {
      event.preventDefault()
      putDown(store)
    }
  },

  render(state, app, store) {
    const letters = inboxStore.get().letters
    const unopened = letters.filter((letter) => !letter.opened).length
    const notices = noticeStore.get().notices
    return html`<section class="screen screen-sheet">
      <p class="label">
        ${app.t('postHeading')} · ${app.t('postSummary', { n: letters.length, unopened })}
      </p>
      ${note(app, store, state)}
      ${state.problem === null ? null : html`<p class="problem" role="alert">${state.problem}</p>`}
      ${
        state.loaded && letters.length === 0 && notices.length === 0
          ? html`<p class="note">${app.t('postEmpty')}</p>`
          : null
      }
      <div class="letters">
        ${notices.map((notice) =>
          state.openNotice === notice.day
            ? openedNotice(app, store, state, notice)
            : sealedNotice(app, store, notice),
        )}
        ${letters.map((summary) =>
          state.open !== null && state.open.publicId === summary.publicId
            ? opened(app, store, state, state.open)
            : sealed(app, store, state, summary),
        )}
      </div>
    </section>`
  },
})

// --- the notices from the Rolls (Q32.2) ----------------------------------------

/** How the Keeper signs: `— {codeName}`, with the persona for a name. */
const keeperLine = (app: App): string =>
  app.t('postSealedFrom', { codeName: app.t('announcePersona') })

/** "The Rolls, on the island of Day 4", or the line for a day before the reckoning. */
const noticeLine = (app: App, notice: Notice): string =>
  notice.dayNumber === null
    ? app.t('noticeSealedUnnumbered')
    : app.t('noticeSealed', { n: notice.dayNumber })

/** A sealed notice: the Rolls, and which island it is about. */
function sealedNotice(app: App, store: Store<State>, notice: Notice): TemplateResult {
  return html`<button
    class="envelope envelope-notice ${notice.opened ? 'envelope-read' : ''}"
    @click=${() => void openTheNotice(app, store, notice)}
  >
    <span class="wax" aria-hidden="true">✦</span>
    <span class="envelope-line">
      <span class="signature">${keeperLine(app)}</span>
      <span class="meta">${noticeLine(app, notice)}</span>
    </span>
  </button>`
}

/** An opened notice: that the island went under, and the finished card. */
function openedNotice(app: App, store: Store<State>, state: State, notice: Notice): TemplateResult {
  return html`<article class="letter-read notice-read" tabindex="-1">
    <header class="letter-head">
      <span class="meta">${noticeLine(app, notice)}</span>
      <span class="signature">${keeperLine(app)}</span>
    </header>
    <p class="prose">${app.t('noticeBody', { name: islandName(app, notice.islandName) })}</p>
    ${
      state.noticeCard === null
        ? null
        : html`<div class="share">
            ${chartCard(app, {
              card: state.noticeCard,
              text: state.noticeText,
              thumbnail: notice.thumbnail,
              copied: state.noticeCopied,
              onCopied: () => store.set({ noticeCopied: true }),
            })}
          </div>`
    }
    <div class="row">
      <button
        class="quiet"
        @click=${() => store.set({ openNotice: null, noticeCard: null, noticeText: null })}
      >
        ${app.t('postHeading')}
      </button>
      <button class="quiet" @click=${() => app.go({ screen: 'island', day: notice.day })}>
        ${app.t('navIsland')}
      </button>
    </div>
  </article>`
}

/**
 * Break a notice's seal: remembered for this player (the client's own mark,
 * not the server's), then the card is asked for as the day's end asks for it.
 * One thing open at a time, as with the letters.
 */
async function openTheNotice(app: App, store: Store<State>, notice: Notice): Promise<void> {
  stopReveal()
  openNotice(app, notice.day)
  store.set({
    open: null,
    prose: '',
    shown: 0,
    openNotice: notice.day,
    noticeCard: null,
    noticeText: null,
    noticeCopied: false,
    problem: null,
  })
  const [card, text] = await Promise.all([
    app.api.shareCard(notice.day),
    app.api.shareText(notice.day),
  ])
  if (store.get().openNotice !== notice.day) return
  if (!card.ok) {
    store.set({ problem: errorText(app, card) })
    return
  }
  store.set({ noticeCard: card.value, noticeText: text.ok ? text.value : null })
  focusOpened()
}

/**
 * The practice shore's slip over the post (#134): what to do with a letter
 * that describes ground the reader has not seen. Its own verb puts it down,
 * and so does Escape; the letter under it is already open.
 */
function note(app: App, store: Store<State>, state: State): TemplateResult | null {
  if (state.note === null) return null
  return slip(app, {
    body: app.t(state.note),
    verbs: [{ key: 'foundNoted', primary: true, press: () => putDown(store) }],
  })
}

function putDown(store: Store<State>): void {
  store.set({ note: null })
  focusOpened()
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The number a day is shown as.
 *
 * The Atlas carries both the day and its number for every island the player has
 * walked, which is where the map comes from. A letter can be about or delivered
 * on a day they never walked -- a held one, or today, before they have landed --
 * so the number is counted on from an island they do know. Days are whole UTC
 * dates, so this is subtraction rather than a rule: where there is no anchor at
 * all, the date itself is shown.
 */
/** Whole UTC days between two game days. */
const daysApart = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY_MS)

/**
 * Whether this letter was actually late.
 *
 * A letter written about island D is deliverable on D+1; anything further is a
 * letter that waited for somebody to appear (Q4.1). The distinction is worth
 * making because the two say different things once the island has gone under
 * (Q18.6): one was delayed in the post, and the other was simply read late.
 */
const wasHeld = (letter: api.ApiInboxLetter): boolean =>
  daysApart(letter.dayAbout, letter.dayDelivered) > 1

function dayOf(state: State, day: string): number | string {
  const known = state.dayNumbers.get(day)
  if (known !== undefined) return known
  const anchor = [...state.dayNumbers.entries()][0]
  if (anchor === undefined) return day
  const [anchorDay, anchorNumber] = anchor
  const number = anchorNumber + daysApart(anchorDay, day)
  return Number.isFinite(number) && number > 0 ? number : day
}

/**
 * "Opened · Day 4", or the line for a day with no number (Q26.8).
 *
 * `dayOf` hands back the date itself when no island of the player's anchors
 * a count -- the practice shore's island, or one before the reckoning -- and
 * a date is not a day number, so it is not shown as one.
 */
function openedLine(app: App, state: State, day: string): string {
  const shown = dayOf(state, day)
  return typeof shown === 'number'
    ? app.t('postOpened', { n: shown })
    : app.t('postOpenedUnnumbered')
}

/** "About the island of Day 4", or the unnumbered line (Q26.8). */
function aboutLine(app: App, state: State, day: string): string {
  const shown = dayOf(state, day)
  return typeof shown === 'number' ? app.t('postAbout', { n: shown }) : app.t('postAboutUnnumbered')
}

/** The envelope's day: "Day 4", or the unnumbered day's own line. */
function sealedLine(app: App, state: State, day: string): string {
  const shown = dayOf(state, day)
  return typeof shown === 'number' ? app.t('dayNumber', { n: shown }) : app.t('dayUnnumbered')
}

/** A sealed envelope: a code name and a day. */
function sealed(
  app: App,
  store: Store<State>,
  state: State,
  summary: api.ApiInboxSummary,
): TemplateResult {
  return html`<button
    class="envelope ${summary.opened ? 'envelope-read' : ''}"
    @click=${() => void openLetter(app, store, summary.publicId)}
  >
    <span class="wax" aria-hidden="true">✦</span>
    <span class="envelope-line">
      <span class="signature">${codeNameLine(app, summary.codeNameId)}</span>
      <span class="meta"
        >${
          summary.opened
            ? openedLine(app, state, summary.dayDelivered)
            : sealedLine(app, state, summary.dayDelivered)
        }</span
      >
    </span>
  </button>`
}

/** An opened letter: the prose, the enclosures, and what may still be done. */
function opened(
  app: App,
  store: Store<State>,
  state: State,
  letter: api.ApiInboxLetter,
): TemplateResult {
  const sunk = !letter.canChart && !letter.canMark
  // `tabindex="-1"` so the focus can be put here once the envelope that had
  // it has been replaced by the letter it opened (#118).
  return html`<article class="letter-read" tabindex="-1">
    <header class="letter-head">
      <span class="meta">${openedLine(app, state, letter.dayDelivered)}</span>
      <span class="signature">${codeNameLine(app, letter.codeNameId)}</span>
    </header>
    <p class="meta">${aboutLine(app, state, letter.dayAbout)}</p>

    <!-- Same split as the island's log (#113): the typewriter is for the eye,
         and the letter a screen reader reads is the whole letter, not
         however much of it had arrived when the reader got there. -->
    <div class="letter-grid">
      <p class="prose" aria-hidden="true">
        ${state.prose.slice(0, state.shown)}<span class="caret" aria-hidden="true"></span>
      </p>
      <p class="offscreen">${state.prose}</p>
      <div class="margin">${marks(app, store, letter)}</div>
    </div>

    ${
      sunk
        ? html`<p class="note">
            ${app.t(wasHeld(letter) ? 'heldLetterSunk' : 'letterIslandSunk')}
          </p>`
        : null
    }
    ${enclosures(app, store, letter)}

    <div class="row">
      <button
        class="quiet"
        @click=${() => {
          stopReveal()
          store.set({ open: null, prose: '', shown: 0 })
          app.go({ screen: 'post', publicId: null })
        }}
      >
        ${app.t('postHeading')}
      </button>
      <button class="quiet" @click=${() => app.go({ screen: 'island', day: letter.dayAbout })}>
        ${app.t('navIsland')}
      </button>
    </div>
  </article>`
}

/** The margin note: what the reader said, or what the ground said (Q17.2). */
function marks(app: App, store: Store<State>, letter: api.ApiInboxLetter): TemplateResult {
  if (letter.mark === 'auto_false') {
    return html`<p class="mark mark-auto">${app.t('markAutoFalse')}</p>`
  }
  if (!letter.canMark) {
    return html`<p class="mark">
      ${letter.mark === null ? app.t('markLocked') : app.t(markKey(letter.mark))}
    </p>`
  }
  if (letter.mark !== null) {
    return html`<p class="mark">
      <span class="quiet-ink">${app.t('marked')}</span>
      <span class="accent">${app.t(markKey(letter.mark))}</span>
      <button
        class="quiet"
        @click=${() =>
          void setMark(app, store, letter, letter.mark === 'trustworthy' ? 'false' : 'trustworthy')}
      >
        ${letter.mark === 'trustworthy' ? app.t('markFalseQuestion') : app.t('markTrustworthy')}
      </button>
    </p>`
  }
  return html`<p class="mark">
    <span class="quiet-ink">${app.t('markAs')}</span>
    <button class="quiet" @click=${() => void setMark(app, store, letter, 'trustworthy')}>
      ${app.t('markTrustworthy')}
    </button>
    <button class="quiet" @click=${() => void setMark(app, store, letter, 'false')}>
      ${app.t('markFalse')}
    </button>
  </p>`
}

const markKey = (mark: 'trustworthy' | 'false'): string =>
  mark === 'trustworthy' ? 'markTrustworthy' : 'markFalse'

/** Attachments, as torn corners. */
function enclosures(
  app: App,
  store: Store<State>,
  letter: api.ApiInboxLetter,
): TemplateResult | null {
  const { slice, relic, fragment } = letter.attachments
  if (slice === undefined && relic === undefined && fragment === undefined) return null
  return html`<div class="enclosures">
    <p class="label">${app.t('enclosures')}</p>
    <div class="torn-row">
      ${
        slice === undefined
          ? null
          : html`<div class="torn-holder">
              <div class="torn">${sliceGrid(slice)}</div>
              <p class="meta">
                ${app.t('sliceAttached', { w: slice.w, h: slice.h })}
                ${
                  letter.charted
                    ? html`<span class="quiet-ink">${app.t('chartedAlready')}</span>`
                    : letter.canChart
                      ? html`<button
                          class="verb verb-primary"
                          @click=${() => void chart(app, store, letter)}
                        >
                          [ ${app.t('chartIt')} ]
                        </button>`
                      : html`<span class="quiet-ink">${app.t('chartUnavailable')}</span>`
                }
              </p>
            </div>`
      }
      ${
        relic === undefined
          ? null
          : html`<div class="torn-holder">
              <div class="torn">
                <span class="glyph accent">${RELIC_GLYPH}</span>
                <span class="coord">${relic.at.x}, ${relic.at.y}</span>
                <p class="serif-note">${relicName(app, relic.relicId)}</p>
              </div>
            </div>`
      }
      ${
        fragment === undefined
          ? null
          : html`<div class="torn-holder">
              <div class="torn">
                <p class="serif-note">${fragmentText(app, fragment.fragmentId)}</p>
              </div>
            </div>`
      }
    </div>
  </div>`
}

async function openLetter(app: App, store: Store<State>, publicId: string): Promise<void> {
  stopReveal()
  store.set({ busy: true, problem: null })
  const result = await app.api.letter(publicId)
  if (!result.ok) {
    store.set({ busy: false, problem: errorText(app, result) })
    return
  }
  const letter = result.value
  const prose = letterProse(app, letter.body)
  // The seal is broken here and in the shell's copy, so the mark beside the
  // post clears without asking the server again (#103).
  updateSummary(letter.publicId, { opened: true })
  // On the practice shore, the first letter opened comes with a word on what
  // to do with it (#134); the slip takes the focus the letter would have.
  const note = practiceSlip(app, 'practiceRead')
  store.set({
    busy: false,
    open: letter,
    prose,
    shown: 0,
    note,
    openNotice: null,
    noticeCard: null,
    noticeText: null,
  })
  reveal(prose, app.reducedMotion, (shown) => store.set({ shown }))
  if (note === null) focusOpened()
  else focusSlip()
}

/**
 * Give the letter the focus the envelope had (#118).
 *
 * Opening one removes the button that was pressed, and the focus would fall
 * to `body` -- so a keyboard reader would have to walk back down the list to
 * reach the letter they just opened. A timeout rather than a microtask,
 * because the render the store asked for is itself queued as one.
 */
function focusOpened(): void {
  window.setTimeout(() => {
    const letter = document.querySelector('.letter-read')
    if (letter instanceof HTMLElement) letter.focus()
  }, 0)
}

/** Charting and marking are one press each: a double tap is not two verdicts. */
async function chart(app: App, store: Store<State>, letter: api.ApiInboxLetter): Promise<void> {
  if (store.get().busy) return
  store.set({ busy: true })
  const result = await app.api.chart(letter.publicId)
  if (!result.ok) {
    store.set({ busy: false, problem: errorText(app, result) })
    return
  }
  store.set({ busy: false, open: { ...letter, charted: true } })
}

async function setMark(
  app: App,
  store: Store<State>,
  letter: api.ApiInboxLetter,
  mark: 'trustworthy' | 'false',
): Promise<void> {
  if (store.get().busy) return
  store.set({ busy: true })
  const result = await app.api.mark(letter.publicId, mark)
  if (!result.ok) {
    store.set({ busy: false, problem: errorText(app, result) })
    return
  }
  updateSummary(letter.publicId, { mark: result.value.mark })
  store.set({ busy: false, open: { ...letter, mark: result.value.mark } })
}
